/* eslint-disable promise/avoid-new */

// need to update dbVersion each time we need to add/update/delete new object store
const dbVersion = 9

let db

export const Stores = {
  backgroundImageStore: 'background_images',
  questionImageStore: 'question_images',
  slideOptionImageStore: 'slide_option_images',
  hintImageStore: 'hint_images',
  contentSlideCategoriesStore: 'content_slide_categories',
  contentSlideCanvasBlocksStore: 'content_slide_blocks',
}

export function initIndexedDB(onErrorCb, onSuccessCb) {
  if (!window || !window.indexedDB) return
  const request = window.indexedDB.open('ahaslides', dbVersion)
  request.onsuccess = (event) => {
    db = event.target.result

    if (onSuccessCb) {
      onSuccessCb(event)
    }
  }
  request.onerror = () => {
    if (onErrorCb) {
      onErrorCb(request.error)
    }
  }
  request.onupgradeneeded = (e) => {
    const Db = e.target.result
    if (!Db.objectStoreNames.contains(Stores.backgroundImageStore)) {
      Db.createObjectStore(Stores.backgroundImageStore, {
        keyPath: 'id',
        autoIncrement: false,
      })
    }
    if (!Db.objectStoreNames.contains(Stores.questionImageStore)) {
      Db.createObjectStore(Stores.questionImageStore, {
        keyPath: 'id',
        autoIncrement: false,
      })
    }
    if (!Db.objectStoreNames.contains(Stores.slideOptionImageStore)) {
      Db.createObjectStore(Stores.slideOptionImageStore, {
        keyPath: 'id',
        autoIncrement: false,
      })
    }
    if (!Db.objectStoreNames.contains(Stores.hintImageStore)) {
      Db.createObjectStore(Stores.hintImageStore, {
        keyPath: 'id',
        autoIncrement: false,
      })
    }
    if (!Db.objectStoreNames.contains(Stores.contentSlideCategoriesStore)) {
      Db.createObjectStore(Stores.contentSlideCategoriesStore, {
        keyPath: 'id',
        autoIncrement: false,
      })
    }
    if (!Db.objectStoreNames.contains(Stores.contentSlideCanvasBlocksStore)) {
      Db.createObjectStore(Stores.contentSlideCanvasBlocksStore, {
        keyPath: 'id',
        autoIncrement: false,
      })
    }
  }
}

// Open an IndexedDB transaction defensively.
//
// `IDBDatabase.transaction()` throws a SYNCHRONOUS `InvalidStateError` ("The
// database connection is closing.") when the connection is closing or already
// closed — which happens routinely as the tab navigates away or when a
// concurrent `versionchange` upgrade forces the current connection shut. Our
// callers here are image/canvas-block CACHE reads and writes: doing one during
// that teardown window is a harmless no-op, but the raw throw was uncaught and
// flooded Sentry (STPANCRAS-PRESENTER-APP-1550, ~12.9k events / 14d, triggered
// on socket reconnect re-caching the presentation detail).
//
// So we swallow that specific transient and return `null`, collapsing the
// closing-connection case into the same silent skip we already do for an
// uninitialised `db`. This does NOT hide genuine faults: real failures of the
// actual read/write still surface via the request's own `onerror`/`reject`.
export function safeTransaction(dbInstance, store, mode) {
  if (!dbInstance) return null
  try {
    return dbInstance.transaction([store], mode)
  } catch (e) {
    // Connection closing/closed — nothing to cache during teardown.
    return null
  }
}

export function setObjectToIndexedDB(key, store, object, onErrorCb, onCompleteCb) {
  const defaultTtl = 7 * 24 * 3600e3 // 7 days

  const transaction = safeTransaction(db, store, 'readwrite')
  if (!transaction) return

  const saveReq = transaction.objectStore(store).put({
    id: key,
    ...object,
    expire: new Date().getTime() + (object.ttl || defaultTtl),
  })
  saveReq.onerror = (e) => {
    if (onErrorCb) onErrorCb(e)
  }
  saveReq.onsuccess = (e) => {
    if (onCompleteCb) {
      onCompleteCb(e)
    }
  }
}

export function removeObjectFromIndexedDB(key, store, onErrorCb, onCompleteCb) {
  if (!key) return
  const transaction = safeTransaction(db, store, 'readwrite')
  if (!transaction) return
  const deleteReq = transaction.objectStore(store).delete(key)
  deleteReq.onerror = (e) => {
    if (onErrorCb) onErrorCb(e)
  }
  deleteReq.onsuccess = (e) => {
    if (onCompleteCb) onCompleteCb(e)
  }
}

export function getAllImageFromIndexedDB(store) {
  const transaction = safeTransaction(db, store, 'readonly')
  if (!transaction) return null
  const readReq = transaction.objectStore(store).getAll()
  return new Promise((resolve, reject) => {
    readReq.onsuccess = (e) => {
      const data = e.target.result
      resolve(data)
    }
    readReq.onerror = (e) => {
      reject(e)
    }
  })
}

export function getObjectFromIndexedDB(slideId, store) {
  const transaction = safeTransaction(db, store, 'readonly')
  if (!transaction) return null
  const readReq = transaction.objectStore(store).get(slideId)
  return new Promise((resolve, reject) => {
    readReq.onsuccess = (e) => {
      const data = e.target.result
      if (!data) {
        return resolve(null)
      }
      if (new Date().getTime() > data.expire) {
        removeObjectFromIndexedDB(slideId, store)
        return resolve(null)
      }
      resolve(data)
    }
    readReq.onerror = (e) => {
      reject(e)
    }
  })
}

export async function updateObjectToIndexedDB(key, store, object, onErrorCb, onCompleteCb) {
  if (!db) return
  const defaultTtl = 7 * 24 * 3600e3 // 7 days

  const currentObject = await getObjectFromIndexedDB(key, store)

  const updatedObject = {
    ...currentObject,
    object,
  }

  const transaction = safeTransaction(db, store, 'readwrite')
  if (!transaction) return

  const saveReq = transaction.objectStore(store).put({
    id: key,
    ...updatedObject,
    expire: new Date().getTime() + (object.ttl || defaultTtl),
  })
  saveReq.onerror = (e) => {
    if (onErrorCb) onErrorCb(e)
  }
  saveReq.onsuccess = (e) => {
    if (onCompleteCb) {
      onCompleteCb(e)
    }
  }
}
